import {
  keepPreviousData,
  type QueryClient,
  useQuery
} from '@tanstack/react-query'
import { useMemo } from 'react'

import {
  organizationsUserGroupsListOptions,
  organizationsUserGroupsListQueryKey
} from 'src/api/main'
import type { UserGroup } from 'src/api/main'

type Query = {
  page?: number
  per_page?: number
  search?: string
  ordering?: string
  /** Only groups that grant at least one role of this application. */
  role_application?: number
}

type Args = {
  workspaceId: number
  enabled?: boolean
  query?: Query
}

const buildQuery = (workspaceId: number, query: Query = {}) => {
  const { search, ordering, page, per_page, role_application } = query

  return {
    organization: workspaceId,
    ...(page && page > 1 ? { page } : {}),
    ...(per_page ? { per_page } : {}),
    ...(search ? { search } : {}),
    ...(ordering ? { ordering } : {}),
    ...(role_application ? { role_application } : {})
  }
}

export const useWorkspaceUserGroups = ({
  workspaceId,
  enabled = true,
  query: queryArgs
}: Args) => {
  const query = useQuery({
    ...organizationsUserGroupsListOptions({
      query: buildQuery(workspaceId, queryArgs)
    }),
    enabled: enabled && Number.isFinite(workspaceId),
    placeholderData: keepPreviousData
  })

  const groups = useMemo<UserGroup[]>(
    () => query.data?.results ?? [],
    [query.data]
  )

  return { ...query, groups, total: query.data?.count ?? groups.length }
}

export const workspaceUserGroupsQueryKey = (workspaceId: number) =>
  organizationsUserGroupsListQueryKey({ query: { organization: workspaceId } })

export const invalidateWorkspaceUserGroups = (
  queryClient: QueryClient,
  workspaceId: number
) =>
  queryClient.invalidateQueries({
    queryKey: workspaceUserGroupsQueryKey(workspaceId),
    refetchType: 'all'
  })
